import {
  Icon,
  IconsName,
  InputErrorMessage,
  Text,
  Tooltip,
} from '@/components';
import APP_CONFIG from '@/config/app';
import useOnClickOrScrollOutside from '@/lib/hooks/useOnClickOrScrollOutside';
import { cva, VariantProps } from 'class-variance-authority';
import {
  FC,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import BarDial from '../BarDial/BarDial';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border border-white'
  : '';
const mainPadding = APP_CONFIG.STYLES.MAIN_PADDING;
const mainGap = APP_CONFIG.STYLES.MAIN_GAP;
const wholeStepDecrement = APP_CONFIG.STYLES.WHOLESTEP_DECREMENT;
const mainRoundedness = APP_CONFIG.STYLES.MAIN_ROUNDEDNESS;
const spacingConfig = {
  padding: `p${mainPadding}`,
  gapBetweenLabelAndGroupOfPrefixInputFieldAndSuffix: `gap${mainGap}`,
  inputFieldInputTypePadding: `px-0 py${mainPadding}${wholeStepDecrement}`,
  inputFieldDropDownTypePadding: `px${mainPadding} py${mainPadding}${wholeStepDecrement}`,
  inputFieldOtpTypePadding: `px-0 py${mainPadding}${wholeStepDecrement}`,
  paddingInPrefixInputFieldAndSuffixBlock: 'p-0',
  prefixPadding: 'p-0',
  suffixPadding: 'p-0',
  inputFieldInputTypeGap: 'gap-0',
  inputFieldDropDownTypeGap: 'gap-0',
  inputFieldOtpTypeGap: 'gap-0',
  gapInPrefixInputFieldAndSuffixBlock: `gap${mainGap}`,
  suffixGap: `gap${mainGap}${wholeStepDecrement}`,
};
const roundednessConfig = {
  rootRoundedness: `rounded${mainRoundedness}`,
  rootRoundednessWhenError: `rounded-t${mainRoundedness} rounded-b-none`,
  inputFieldRoundedness: `rounded${mainRoundedness}`,
};
const sizingConfig = {
  rootWidth: 'w-full',
  inputFieldWidth: 'w-full',
  inputFieldPadding: 'px-0',
  labelTextPrimary: 'text-xs',
  labelTextError: 'text-xs',
  labelTextDigitError: 'text-xs',
  labelTextFocused: 'text-xs',
  labelTextDisabled: 'text-xs',
  labelTextReadOnly: 'text-xs',
  subtitleText: 'xsm' as `xsm`,
  inputFieldInputTypeWidth: 'w-full',
  inputFieldInputTypeHeight: 'h-full',
  inputFieldInputTypeTextSize: 'text-md',
  inputFieldDropdownTypeWidth: 'w-full',
  inputFieldDropdownTypeHeight: 'h-full',
  inputFieldDropdownTypeTextSize: 'text-md',
  inputFieldOtpTypeWidth: 'w-full',
  inputFieldOtpTypeHeight: 'h-full',
  inputFieldOtpTypeTextSize: 'text-2xl font-semibold text-center',
};
const inputColorClasses = {
  backgroundPrimaryBase: 'bg-primary/5',
  borderPrimaryBase: 'border border-primary/5',
  backgroundPrimaryHover: 'hover:bg-primary/10',
  borderPrimaryHover: 'hover:border-primary/10',
  backgroundPrimaryFocus: 'focus-within:bg-primary/15',
  borderPrimaryFocus: 'focus-within:border-primary/15',
  backgroundPrimaryActive: 'active:bg-primary/15',
  borderPrimaryActive: 'active:border-primary/15',
  placeholderTextPrimary: 'placeholder:text-primary',
  labelTextPrimary: 'text-secondary',
  subtitleTextPrimary: 'secondary' as 'secondary',
  backgroundFocusedBase: 'bg-primary/15',
  borderFocusedBase: 'border border-primary/15',
  backgroundFocusedHover: 'hover:bg-primary/15',
  borderFocusedHover: 'hover:border-primary/15',
  backgroundFocusedFocus: 'focus-within:bg-primary/15',
  borderFocusedFocus: 'focus-within:border-primary/15',
  backgroundFocusedActive: 'active:bg-primary/15',
  borderFocusedActive: 'active:border-primary/15',
  labelTextFocused: 'text-primary',
  subtitleTextFocused: 'primary' as 'primary',
  placeholderTextFocused: 'placeholder:text-primary',
  backgroundErrorBase: 'bg-error/10',
  borderErrorBase: 'border border-error/10',
  backgroundErrorHover: 'hover:bg-error/15',
  borderErrorHover: 'hover:border-error/15',
  backgroundErrorFocus: 'focus-within:bg-error/20',
  borderErrorFocus: 'focus-within:border-error/20',
  backgroundErrorActive: 'active:bg-error/20',
  borderErrorActive: 'active:border-error/20',
  labelTextError: 'text-error',
  subtitleTextError: 'error' as 'error',
  placeholderTextError: 'placeholder:text-error',
  backgroundDigitErrorBase: 'bg-transparent',
  borderDigitErrorBase: 'border-none',
  backgroundDigitErrorHover: 'hover:bg-error/15',
  borderDigitErrorHover: 'hover:border-error/15',
  backgroundDigitErrorFocus: 'focus-within:bg-error/20',
  borderDigitErrorFocus: 'focus-within:border-error/20',
  backgroundDigitErrorActive: 'active:bg-error/20',
  borderDigitErrorActive: 'active:border-error/20',
  labelTextDigitError: 'text-error',
  subtitleTextDigitError: 'error' as 'error',
  placeholderTextDigitError: 'placeholder:text-error',
  backgroundDisabledBase: 'bg-primary/5',
  borderDisabledBase: 'border border-primary/5',
  backgroundDisabledHover: 'hover:bg-primary/5',
  borderDisabledHover: 'hover:border-primary/5',
  backgroundDisabledFocus: 'focus-within:bg-primary/5',
  borderDisabledFocus: 'focus-within:border-primary/5',
  backgroundDisabledActive: 'active:bg-primary/5',
  borderDisabledActive: 'active:border-primary/5',
  labelTextDisabled: 'text-secondary/80',
  subtitleTextDisabled: 'tertiary' as 'tertiary',
  placeholderTextDisabled: 'placeholder:text-secondary/80',
  backgroundReadOnlyBase: 'bg-primary/5',
  borderReadOnlyBase: 'border border-primary/5',
  backgroundReadOnlyHover: 'hover:bg-primary/5',
  borderReadOnlyHover: 'hover:border-primary/5',
  backgroundReadOnlyFocus: 'focus-within:bg-primary/5',
  borderReadOnlyFocus: 'focus-within:border-primary/5',
  backgroundReadOnlyActive: 'active:bg-primary/5',
  borderReadOnlyActive: 'active:border-primary/5',
  labelTextReadOnly: 'text-secondary',
  subtitleTextReadOnly: 'tertiary' as 'tertiary',
  placeholderTextReadOnly: 'placeholder:text-secondary',
  backgroundDropdownBase: 'bg-primary/5',
  borderDropdownBase: 'border border-primary/5',
  backgroundDropdownHover: 'hover:bg-primary/10',
  borderDropdownHover: 'hover:border-primary/10',
  backgroundDropdownFocus: 'focus-within:bg-primary/15',
  borderDropdownFocus: 'focus-within:border-primary/15',
  backgroundDropdownActive: 'active:bg-primary/15',
  borderDropdownActive: 'active:border-primary/15',
  labelTextDropdown: 'text-secondary',
  subtitleTextDropdown: 'tertiary' as 'tertiary',
  inputFieldBackgroundPrimaryBase: 'bg-transparent',
  inputFieldBorderPrimaryBase: 'border-none',
  inputFieldTextPrimaryBase: 'text-secondary',
  inputFieldBackgroundPrimaryHover: 'hover:bg-transparent',
  inputFieldBorderPrimaryHover: 'hover:border-none',
  inputFieldBackgroundPrimaryFocus: 'focus-within:bg-transparent',
  inputFieldBorderPrimaryFocus: 'focus-within:border-none',
  inputFieldBackgroundPrimaryActive: 'active:bg-transparent',
  inputFieldBorderPrimaryActive: 'active:border-none',
  inputFieldBackgroundFocusedBase: 'bg-transparent',
  inputFieldBorderFocusedBase: 'border-none',
  inputFieldTextFocusedBase: 'text-primary',
  inputFieldBackgroundFocusedHover: 'hover:bg-transparent',
  inputFieldBorderFocusedHover: 'hover:border-none',
  inputFieldBackgroundFocusedFocus: 'focus-within:bg-transparent',
  inputFieldBorderFocusedFocus: 'focus-within:border-none',
  inputFieldBackgroundFocusedActive: 'active:bg-transparent',
  inputFieldBorderFocusedActive: 'active:border-none',
  inputFieldBackgroundErrorBase: 'bg-transparent',
  inputFieldBorderErrorBase: 'border-none',
  inputFieldTextErrorBase: 'text-error',
  inputFieldBackgroundErrorHover: 'hover:bg-transparent',
  inputFieldBorderErrorHover: 'hover:border-none',
  inputFieldBackgroundErrorFocus: 'focus-within:bg-transparent',
  inputFieldBorderErrorFocus: 'focus-within:border-none',
  inputFieldBackgroundErrorActive: 'active:bg-transparent',
  inputFieldBorderErrorActive: 'active:border-none',
  inputFieldBackgroundDigitErrorBase: 'bg-transparent',
  inputFieldBorderDigitErrorBase: 'border-none',
  inputFieldTextDigitErrorBase: 'text-error',
  inputFieldBackgroundDigitErrorHover: 'hover:bg-transparent',
  inputFieldBorderDigitErrorHover: 'hover:border-none',
  inputFieldBackgroundDigitErrorFocus: 'focus-within:bg-transparent',
  inputFieldBorderDigitErrorFocus: 'focus-within:border-none',
  inputFieldBackgroundDigitErrorActive: 'active:bg-transparent',
  inputFieldBorderDigitErrorActive: 'active:border-none',
  inputFieldBackgroundDisabledBase: 'bg-transparent',
  inputFieldBorderDisabledBase: 'border-none',
  inputFieldTextDisabledBase: 'text-secondary',
  inputFieldBackgroundDisabledHover: 'hover:bg-transparent',
  inputFieldBorderDisabledHover: 'hover:border-none',
  inputFieldBackgroundDisabledFocus: 'focus-within:bg-transparent',
  inputFieldBorderDisabledFocus: 'focus-within:border-none',
  inputFieldBackgroundDisabledActive: 'active:bg-transparent',
  inputFieldBorderDisabledActive: 'active:border-none',
  inputFieldBackgroundReadOnlyBase: 'bg-transparent',
  inputFieldBorderReadOnlyBase: 'border-none',
  inputFieldTextReadOnlyBase: 'text-secondary',
  inputFieldBackgroundReadOnlyHover: 'hover:bg-transparent',
  inputFieldBorderReadOnlyHover: 'hover:border-none',
  inputFieldBackgroundReadOnlyFocus: 'focus-within:bg-transparent',
  inputFieldBorderReadOnlyFocus: 'focus-within:border-none',
  inputFieldBackgroundReadOnlyActive: 'active:bg-transparent',
  inputFieldBorderReadOnlyActive: 'active:border-none',
  inputFieldTextDropdownBase: 'bg-transparent',
};
export const inputStyles = cva(
  `block appearance-none focus:outline-none focus:ring-0 peer 
  ${sizingConfig.inputFieldWidth} 
  ${sizingConfig.inputFieldPadding}
  ${roundednessConfig.inputFieldRoundedness} `,
  {
    variants: {
      intent: {
        primary: [
          inputColorClasses.inputFieldBackgroundPrimaryBase,
          inputColorClasses.inputFieldBorderPrimaryBase,
          inputColorClasses.inputFieldBackgroundPrimaryHover,
          inputColorClasses.inputFieldBorderPrimaryHover,
          inputColorClasses.inputFieldBackgroundPrimaryFocus,
          inputColorClasses.inputFieldBorderPrimaryFocus,
          inputColorClasses.inputFieldBackgroundPrimaryActive,
          inputColorClasses.inputFieldBorderPrimaryActive,
          inputColorClasses.inputFieldTextPrimaryBase,
          inputColorClasses.placeholderTextPrimary,
        ],
        focused: [
          inputColorClasses.inputFieldBackgroundFocusedBase,
          inputColorClasses.inputFieldBorderFocusedBase,
          inputColorClasses.inputFieldBackgroundFocusedHover,
          inputColorClasses.inputFieldBorderFocusedHover,
          inputColorClasses.inputFieldBackgroundFocusedFocus,
          inputColorClasses.inputFieldBorderFocusedFocus,
          inputColorClasses.inputFieldBackgroundFocusedActive,
          inputColorClasses.inputFieldBorderFocusedActive,
          inputColorClasses.inputFieldTextFocusedBase,
          inputColorClasses.placeholderTextFocused,
        ],
        error: [
          inputColorClasses.inputFieldBackgroundErrorBase,
          inputColorClasses.inputFieldBorderErrorBase,
          inputColorClasses.inputFieldBackgroundErrorHover,
          inputColorClasses.inputFieldBorderErrorHover,
          inputColorClasses.inputFieldBackgroundErrorFocus,
          inputColorClasses.inputFieldBorderErrorFocus,
          inputColorClasses.inputFieldBackgroundErrorActive,
          inputColorClasses.inputFieldBorderErrorActive,
          inputColorClasses.inputFieldTextErrorBase,
          inputColorClasses.placeholderTextError,
        ],
        digitError: [
          inputColorClasses.inputFieldBackgroundDigitErrorBase,
          inputColorClasses.inputFieldBorderDigitErrorBase,
          inputColorClasses.inputFieldBackgroundDigitErrorHover,
          inputColorClasses.inputFieldBorderDigitErrorHover,
          inputColorClasses.inputFieldBackgroundDigitErrorFocus,
          inputColorClasses.inputFieldBorderDigitErrorFocus,
          inputColorClasses.inputFieldBackgroundDigitErrorActive,
          inputColorClasses.inputFieldBorderDigitErrorActive,
          inputColorClasses.inputFieldTextDigitErrorBase,
          inputColorClasses.placeholderTextDigitError,
        ],
      },
      size: {
        input: [
          sizingConfig.inputFieldInputTypeWidth,
          sizingConfig.inputFieldInputTypeHeight,
          sizingConfig.inputFieldInputTypeTextSize,
          spacingConfig.inputFieldInputTypePadding,
          spacingConfig.inputFieldInputTypeGap,
        ],
        dropdown: [
          sizingConfig.inputFieldDropdownTypeWidth,
          sizingConfig.inputFieldDropdownTypeHeight,
          sizingConfig.inputFieldDropdownTypeTextSize,
          spacingConfig.inputFieldDropDownTypePadding,
          spacingConfig.inputFieldDropDownTypeGap,
          'cursor-pointer',
        ],
        otp: [
          sizingConfig.inputFieldOtpTypeWidth,
          sizingConfig.inputFieldOtpTypeHeight,
          sizingConfig.inputFieldOtpTypeTextSize,
          spacingConfig.inputFieldOtpTypePadding,
          spacingConfig.inputFieldOtpTypeGap,
        ],
      },
      showError: {
        true: [],
        false: [],
      },
    },
    defaultVariants: {
      intent: 'primary',
      size: 'input',
    },
  },
);
export const labelVariants = cva(`relative flex`, {
  variants: {
    intent: {
      primary: [
        sizingConfig.labelTextPrimary,
        inputColorClasses.labelTextPrimary,
      ],
      error: [sizingConfig.labelTextError, inputColorClasses.labelTextError],
      digitError: [
        sizingConfig.labelTextDigitError,
        inputColorClasses.labelTextDigitError,
      ],
      focused: [
        sizingConfig.labelTextFocused,
        inputColorClasses.labelTextFocused,
      ],
      disabled: [
        sizingConfig.labelTextDisabled,
        inputColorClasses.labelTextDisabled,
      ],
      readOnly: [
        sizingConfig.labelTextReadOnly,
        inputColorClasses.labelTextReadOnly,
      ],
    },
  },
  defaultVariants: {
    intent: 'primary',
  },
});
export type InputProps = {
  label?: string;
  subtitle?: string;
  prefix?: ReactNode;
  postfixToInputValue?: string;
  suffix?: ReactNode;
  suffixIconName?: IconsName;
  tooltip?: ReactNode;
  tooltipClassName?: string;
  tooltipIsFullBleed?: boolean;
  showPercentageDial?: boolean;
  maxAmount?: number;
  errorMessage?: string;
  clearErrors?: () => void;
  onIconClick?: () => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> &
  VariantProps<typeof inputStyles>;
const Input: FC<InputProps> = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      name,
      subtitle,
      label,
      intent = 'primary',
      size,
      prefix,
      postfixToInputValue,
      suffixIconName: suffixIcon,
      suffix: suffixBody,
      tooltip: suffixTooltip,
      tooltipClassName = '',
      tooltipIsFullBleed = false,
      showPercentageDial = false,
      maxAmount = 100,
      errorMessage,
      onIconClick: onSuffixIconClick,
      className,
      showError,
      type = 'text',
      step,
      value,
      defaultValue,
      onChange,
      clearErrors,
      disabled,
      readOnly,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [localValue, setLocalValue] = useState<string>(
      defaultValue !== undefined ? String(defaultValue) : '',
    );
    const [percentage, setPercentage] = useState(0);
    useEffect(() => {
      if (defaultValue !== undefined) {
        const numericValue = parseFloat(String(defaultValue)) || 0;
        setLocalValue(String(numericValue));
        if (maxAmount && maxAmount > 0) {
          setPercentage((numericValue / maxAmount) * 100);
        } else {
          setPercentage(0);
        }
      }
    }, [defaultValue, maxAmount]);
    useEffect(() => {
      const numericValue = parseFloat(String(defaultValue)) || 0;
      if (maxAmount && maxAmount > 0) {
        setPercentage((numericValue / maxAmount) * 100);
      } else {
        setPercentage(0);
      }
    }, [localValue, maxAmount]);
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value);
      onChange?.(e);
    };
    const handleDialChange = (newPercentage: number) => {
      const clampedPercentage = Math.min(100, Math.max(0, newPercentage));
      setPercentage(clampedPercentage);
      const newValue = ((clampedPercentage / 100) * (maxAmount ?? 100)).toFixed(
        2,
      );
      if (inputRef.current) {
        inputRef.current.value = newValue;
        const changeEvent = new Event('input', { bubbles: true });
        Object.defineProperty(changeEvent, 'target', {
          writable: false,
          value: inputRef.current,
        });
        onChange?.(
          changeEvent as unknown as React.ChangeEvent<HTMLInputElement>,
        );
      }
    };
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
    useOnClickOrScrollOutside(containerRef, () => {
      if (clearErrors) {
        clearErrors();
      }
    });
    useEffect(() => {
      if (inputRef.current && containerRef.current) {
        const inputPaddingLeft = window.getComputedStyle(
          inputRef.current,
        ).paddingLeft;
        inputRef.current.style.setProperty('--label-left', inputPaddingLeft);
      }
    }, [inputRef, containerRef]);
    const getScrollParents = (element: HTMLElement): (Element | Window)[] => {
      const scrollParents: (Element | Window)[] = [];
      let parent: Node | null = element.parentNode;
      while (parent && parent.nodeType === Node.ELEMENT_NODE) {
        const el = parent as Element;
        const overflowY = window.getComputedStyle(el).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          scrollParents.push(el);
        }
        parent = el.parentNode;
      }
      scrollParents.push(window);
      return scrollParents;
    };
    useEffect(() => {
      if (containerRef.current && clearErrors) {
        const element = containerRef.current;
        const scrollParents = getScrollParents(element);
        const handleScroll = () => {
          clearErrors();
        };
        scrollParents.forEach((scrollParent) => {
          scrollParent.addEventListener('scroll', handleScroll);
        });
        return () => {
          scrollParents.forEach((scrollParent) => {
            scrollParent.removeEventListener('scroll', handleScroll);
          });
        };
      }
    }, [containerRef, clearErrors]);
    useEffect(() => {
      const input = inputRef.current;
      if (input && type === 'number') {
        const handleWheel = (e: WheelEvent) => {
          e.preventDefault();
        };
        input.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
          input.removeEventListener('wheel', handleWheel);
        };
      }
    }, [type]);
    return (
      <div
        ref={containerRef}
        className={`relative flex-col
          ${spacingConfig.padding} 
          ${sizingConfig.rootWidth} 
          ${
            intent === 'primary' ? inputColorClasses.backgroundPrimaryBase : ''
          } 
          ${intent === 'primary' ? inputColorClasses.borderPrimaryBase : ''} 
          ${
            intent === 'primary' ? inputColorClasses.backgroundPrimaryHover : ''
          } 
          ${intent === 'primary' ? inputColorClasses.borderPrimaryHover : ''} 
          ${
            intent === 'primary' ? inputColorClasses.backgroundPrimaryFocus : ''
          } 
          ${intent === 'primary' ? inputColorClasses.borderPrimaryFocus : ''} 
          ${
            intent === 'primary'
              ? inputColorClasses.backgroundPrimaryActive
              : ''
          } 
          ${intent === 'primary' ? inputColorClasses.borderPrimaryActive : ''} 
          ${
            intent === 'primary'
              ? inputColorClasses.inputFieldTextPrimaryBase
              : ''
          } 
          ${
            intent === 'focused' ? inputColorClasses.backgroundFocusedBase : ''
          } 
          ${intent === 'focused' ? inputColorClasses.borderFocusedBase : ''} 
          ${
            intent === 'focused' ? inputColorClasses.backgroundFocusedHover : ''
          }
          ${intent === 'focused' ? inputColorClasses.borderFocusedHover : ''} 
          ${
            intent === 'focused' ? inputColorClasses.backgroundFocusedFocus : ''
          } ${intent === 'focused' ? inputColorClasses.borderFocusedFocus : ''} 
          ${
            intent === 'focused'
              ? inputColorClasses.backgroundFocusedActive
              : ''
          } 
          ${intent === 'focused' ? inputColorClasses.borderFocusedActive : ''} 
          ${
            intent === 'focused'
              ? inputColorClasses.inputFieldTextFocusedBase
              : ''
          } 
          ${intent === 'error' ? inputColorClasses.backgroundErrorBase : ''} 
          ${intent === 'error' ? inputColorClasses.borderErrorBase : ''} 
          ${intent === 'error' ? inputColorClasses.backgroundErrorHover : ''} 
          ${intent === 'error' ? inputColorClasses.borderErrorHover : ''} 
          ${intent === 'error' ? inputColorClasses.backgroundErrorFocus : ''} 
          ${intent === 'error' ? inputColorClasses.borderErrorFocus : ''} 
          ${intent === 'error' ? inputColorClasses.backgroundErrorActive : ''} 
          ${intent === 'error' ? inputColorClasses.borderErrorActive : ''} 
          ${
            intent === 'error' ? inputColorClasses.inputFieldTextErrorBase : ''
          } 
          ${
            intent === 'digitError'
              ? inputColorClasses.backgroundDigitErrorBase
              : ''
          } 
          ${
            intent === 'digitError'
              ? inputColorClasses.borderDigitErrorBase
              : ''
          } 
          ${
            intent === 'digitError'
              ? inputColorClasses.backgroundDigitErrorHover
              : ''
          } 
          ${
            intent === 'digitError'
              ? inputColorClasses.borderDigitErrorHover
              : ''
          } 
          ${
            intent === 'digitError'
              ? inputColorClasses.backgroundDigitErrorFocus
              : ''
          } 
          ${
            intent === 'digitError'
              ? inputColorClasses.borderDigitErrorFocus
              : ''
          } 
          ${
            intent === 'digitError'
              ? inputColorClasses.backgroundDigitErrorActive
              : ''
          } 
          ${
            intent === 'digitError'
              ? inputColorClasses.borderDigitErrorActive
              : ''
          } 
          ${
            intent === 'digitError'
              ? inputColorClasses.inputFieldTextDigitErrorBase
              : ''
          } 
          ${readOnly ? inputColorClasses.backgroundReadOnlyBase : ''} 
          ${readOnly ? inputColorClasses.borderReadOnlyBase : ''} 
          ${readOnly ? inputColorClasses.backgroundReadOnlyHover : ''} 
          ${readOnly ? inputColorClasses.borderReadOnlyHover : ''} 
          ${readOnly ? inputColorClasses.backgroundReadOnlyFocus : ''} 
          ${readOnly ? inputColorClasses.borderReadOnlyFocus : ''} 
          ${readOnly ? inputColorClasses.backgroundReadOnlyActive : ''} 
          ${readOnly ? inputColorClasses.borderReadOnlyActive : ''} 
          ${readOnly ? inputColorClasses.inputFieldTextReadOnlyBase : ''} 
          ${readOnly ? 'cursor-default' : ''} 
          ${disabled ? inputColorClasses.backgroundDisabledBase : ''} 
          ${disabled ? inputColorClasses.borderDisabledBase : ''} 
          ${disabled ? inputColorClasses.backgroundDisabledBase : ''} 
          ${disabled ? inputColorClasses.borderDisabledBase : ''} 
          ${disabled ? inputColorClasses.backgroundDisabledHover : ''} 
          ${disabled ? inputColorClasses.borderDisabledHover : ''} 
          ${disabled ? inputColorClasses.backgroundDisabledFocus : ''} 
          ${disabled ? inputColorClasses.borderDisabledFocus : ''} 
          ${disabled ? inputColorClasses.backgroundDisabledActive : ''} 
          ${disabled ? inputColorClasses.borderDisabledActive : ''} 
          ${disabled ? inputColorClasses.inputFieldTextDisabledBase : ''} 
          ${disabled ? 'cursor-default' : ''} 
          ${
            showError || errorMessage
              ? roundednessConfig.rootRoundednessWhenError
              : roundednessConfig.rootRoundedness
          } 
          ${borderClassToVisualizeElementsAndSpacing}`}
      >
        <div
          className={`flex flex-col ${spacingConfig.gapBetweenLabelAndGroupOfPrefixInputFieldAndSuffix}`}
        >
          {label && (
            <label
              htmlFor={name}
              className={`${labelVariants({
                intent: disabled || readOnly ? 'disabled' : intent,
              })} ${borderClassToVisualizeElementsAndSpacing}`}
            >
              {label}
            </label>
          )}
          <div
            className={`relative flex items-center 
            ${spacingConfig.paddingInPrefixInputFieldAndSuffixBlock} 
            ${spacingConfig.gapInPrefixInputFieldAndSuffixBlock} 
            ${borderClassToVisualizeElementsAndSpacing}`}
          >
            {prefix && (
              <div
                className={`flex items-center ${spacingConfig.prefixPadding} ${borderClassToVisualizeElementsAndSpacing}`}
              >
                {prefix}
              </div>
            )}
            <div
              className={`flex-1 flex-col justify-center ${borderClassToVisualizeElementsAndSpacing}`}
            >
              <div className="flex items-center w-full">
                <input
                  ref={inputRef}
                  id={name}
                  name={name}
                  type={type}
                  step={step}
                  value={value}
                  onChange={handleInputChange}
                  className={`${inputStyles({
                    intent: intent,
                    size,
                    showError,
                  })} ${borderClassToVisualizeElementsAndSpacing}`}
                  autoComplete="off"
                  aria-label={label}
                  aria-describedby={`${name}-error`}
                  disabled={disabled}
                  readOnly={readOnly || size === 'dropdown'}
                  {...props}
                />
                {Boolean(postfixToInputValue?.trim()) && (
                  <span
                    className={`${inputStyles({
                      intent: intent,
                      size,
                      showError,
                    })} ${borderClassToVisualizeElementsAndSpacing}`}
                  >
                    {postfixToInputValue}
                  </span>
                )}
              </div>
              <div className={`flex flex-col gap-md`}>
                {Boolean(subtitle?.trim()) && (
                  <Text
                    value={subtitle}
                    size={sizingConfig.subtitleText}
                    color={
                      intent === 'primary'
                        ? inputColorClasses.subtitleTextPrimary
                        : intent === 'focused'
                        ? inputColorClasses.subtitleTextFocused
                        : intent === 'error'
                        ? inputColorClasses.subtitleTextError
                        : intent === 'digitError'
                        ? inputColorClasses.subtitleTextDigitError
                        : disabled
                        ? inputColorClasses.subtitleTextDisabled
                        : readOnly
                        ? inputColorClasses.subtitleTextReadOnly
                        : inputColorClasses.subtitleTextPrimary
                    }
                    className={`${borderClassToVisualizeElementsAndSpacing}`}
                  />
                )}
              </div>
            </div>
            <div
              className={`flex items-center ${spacingConfig.suffixPadding} ${spacingConfig.suffixGap} ${borderClassToVisualizeElementsAndSpacing}`}
            >
              {suffixIcon && onSuffixIconClick && (
                <button
                  title="Icon"
                  type="button"
                  onClick={() => onSuffixIconClick()}
                  className={`flex items-center ${
                    disabled ? 'cursor-default' : 'cursor-pointer'
                  } ${borderClassToVisualizeElementsAndSpacing}`}
                  disabled={disabled}
                >
                  <Icon
                    name={suffixIcon}
                    intent="accent"
                    className={`${borderClassToVisualizeElementsAndSpacing}`}
                  />
                </button>
              )}
              {suffixBody && (
                <div
                  className={`flex items-center ${
                    disabled ? 'cursor-default' : 'cursor-pointer'
                  } ${borderClassToVisualizeElementsAndSpacing}`}
                >
                  {suffixBody}
                </div>
              )}
              {suffixTooltip && (
                <Tooltip
                  trigger={<Icon name="question" intent="accent" />}
                  align="right"
                  alignToElementRef={containerRef}
                  className={`${tooltipClassName} cursor-pointer ${borderClassToVisualizeElementsAndSpacing}`}
                  isFullBleed={tooltipIsFullBleed}
                >
                  {suffixTooltip}
                </Tooltip>
              )}
            </div>
          </div>
          {showPercentageDial && type === 'number' && (
            <BarDial
              value={percentage}
              onChange={handleDialChange}
              editable
              mode="full"
              showBreakpoints
              showPercentageLabels
            />
          )}
        </div>
        {errorMessage && (
          <InputErrorMessage
            message={errorMessage}
            className={borderClassToVisualizeElementsAndSpacing}
            attachToElementRef={containerRef}
          />
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';
export default Input;
